import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { projectFamilies, stateLabel } from './libraryFamilies';
import { setDisplayPreferences, useDisplayPreferences } from '../hooks/useDisplayPreferences';
import { useFoldedRows } from '../hooks/useFoldedRows';
import { CalibrationPill, DatesPill, DbPill, DensityToggle, FamilyPill, FoldButton, GradingPill, ProgressPill, RowCells, StateControl } from './projectPills';
import { familyMatchesShow, parseShow, SHOW_OPTIONS } from './libraryShow';
import { usePlans } from './header/useCurrentPlan';
import LibraryPlans from './director/LibraryPlans';
import { stage, stageShort } from './director/planCardModel';
import type { DirectorPlanRow } from '../api/directorTypes';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { Merge, Search } from 'lucide-react';
import PillSelect from './toolbar/PillSelect';
import { apiClient } from '../api/client';
import type {
  ExportChoice,
  ProjectOverview,
  ProjectRecentImage,
  TargetOverview,
  DateRange,
} from '../api/types';
import {
  useAllDatabases,
  useMergedProjectsOverview,
  useMergedTargetsOverview,
  useMergedOverallStats,
  useCalibrationHealth,
  type WithDb,
} from '../hooks/useDatabases';
import { isTauriApp, tauriFileSystem } from '../utils/tauri';
import { describeExportProgress, useExportJob } from '../hooks/useExportJob';
import { openSettings } from '../utils/settingsIntent';
import {
  loadProjectSeenState,
  markerForProject,
  newImageCount,
  newTargetImageCount,
  projectSeenKey,
  revisitProject,
  revisitTarget,
  saveProjectSeenState,
} from '../utils/projectRecency';
import { formatRelativeTime } from '../utils/relativeTime';
import { keepScoringInScope, useDbProjectTarget, useUrlParams } from '../hooks/useUrlState';
import { imageDetailPath } from '../utils/imageDetailRoutes';
import {
  groupProjectsByActivity,
  INACTIVE_PROJECT_STATE,
  isArchivedProject,
  isRecentProject,
  projectMatchesSearch,
  sortProjects,
  type ProjectSort,
} from '../utils/projectNavigation';
import CalibrationReportDialog from './CalibrationReportDialog';
import ExportDialog, { type ExportRequest } from './ExportDialog';
import AstroBinExportDialog, { type AstroBinExportRequest } from './AstroBinExportDialog';
import WbppRunDialog, { type WbppRunRequest } from './WbppRunDialog';
import type { WbppRunStatus } from '../api/types';
import {
  describeQueuedRun,
  describeWbppRun,
  describeWbppRunForProject,
  isRunOfInterest,
  useWbppRuns,
  wbppRunTone,
} from '../hooks/useWbppRun';
import { DEFAULT_WBPP_OPTIONS } from '../api/types';
import { commonDirectory } from '../utils/commonDirectory';
import OrganizationDialog, { type OrganizationScope } from './OrganizationDialog';
import ProjectDeleteDialog, { type ProjectDeleteRequest } from './ProjectDeleteDialog';
import DeletedProjects from './DeletedProjects';
import { useAccess } from '../auth/access';
import PreviewImage from './PreviewImage';
import { useColorPreview } from '../hooks/useColorPreview';
import { flatWarningNote, uncalibratedNote } from '../utils/uncalibratedNote';
import { useNavigateNow } from '../hooks/useNavigateNow';
import './projectCard.css';
import './Overview.css';

/// Inline edit state for correcting imported groupings.
type Organizing =
  | { kind: 'project'; dbId: string; id: number; name: string; mergeInto: string }
  | { kind: 'target'; dbId: string; id: number; projectId: number; name: string };

function recentImageKey(dbId: string, projectId: number, imageId: number): string {
  return `${dbId}:${projectId}:${imageId}`;
}

function projectKey(dbId: string, projectId: number): string {
  return `${dbId}:${projectId}`;
}


export default function Overview() {
  const color = useColorPreview();
  const navigate = useNavigate();
  const navigateNow = useNavigateNow();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [projectSort, setProjectSort] = useState<ProjectSort>('recent');
  // Narrows the projects list to one catalog. Lives in URL state (`dbfilter`)
  // so reload restores it. Distinct from the parked `db` return-scope slug,
  // which marks where the user came from and never filters this view.
  const { getParam, updateParams } = useUrlParams();
  const dbFilter = getParam('dbfilter');
  // Plans from Planning: which project rows belong to one, and the plans no
  // database shoots yet. Nothing is asked when Planning is off.
  const plans = usePlans();
  // What to show and the search text are URL state too, so a reload or a
  // shared link lands on the same list.
  const show = parseShow(getParam('show'), plans.enabled);
  const projectSearch = getParam('q') ?? '';
  const setProjectSearch = (value: string) => updateParams({ q: value });
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [organizing, setOrganizing] = useState<Organizing | null>(null);
  const [organizationScope, setOrganizationScope] = useState<OrganizationScope | null>(null);
  const [organizeBusy, setOrganizeBusy] = useState(false);
  const [organizeError, setOrganizeError] = useState('');
  const [seenProjects, setSeenProjects] = useState(loadProjectSeenState);
  const [relativeNow, setRelativeNow] = useState(Date.now);
  // Compact rows for browsing, the full card on request. The default lives
  // with the other display preferences (Settings, Review); the toolbar
  // toggle changes the same setting. Any row can be opened or folded on
  // its own in either view, and the project the user came from opens by
  // itself until they fold it.
  const displayPreferences = useDisplayPreferences();
  const density = displayPreferences.libraryDensity;
  const folds = useFoldedRows(density);
  const showsCard = folds.showsCard;
  const toggleCard = folds.toggle;
  const chooseDensity = (next: 'compact' | 'detailed') => {
    setDisplayPreferences({ ...displayPreferences, libraryDensity: next });
    // A new default starts clean: rows opened or folded by hand go back to it.
    folds.reset();
  };
  const [calibrationReportProject, setCalibrationReportProject] = useState<{
    dbId: string;
    id: number;
    name: string;
  } | null>(null);

  const { data: databases } = useAllDatabases();
  const { data: serverInfo } = useQuery({
    queryKey: ['serverInfo'],
    queryFn: apiClient.getServerInfo,
    staleTime: 5 * 60 * 1000,
  });
  const { data: overallStats, isLoading: statsLoading } = useMergedOverallStats();
  const { data: projects, isLoading: projectsLoading, isError: projectsIncomplete } = useMergedProjectsOverview();
  const { data: targets, isLoading: targetsLoading } = useMergedTargetsOverview();
  // What the background calibration check found, by db:project. Only
  // current results; a project without one shows no pill.
  const calibrationHealth = useCalibrationHealth();
  const openCalibrationReport = (project: WithDb<ProjectOverview>) =>
    setCalibrationReportProject({
      dbId: project.db_id,
      id: project.id,
      name: project.display_name,
    });
  const { canWrite } = useAccess();
  // Moving targets and exposures edits catalog rows: an editor's work.
  const organizeAllowed = canWrite;
  // Deleting a project changes a database the server was pointed at: it
  // needs database management as well.
  const deleteAllowed = canWrite && (serverInfo?.allow_database_management ?? false);
  const [deleting, setDeleting] = useState<{ request: ProjectDeleteRequest; inArchive: boolean } | null>(null);
  const openDelete = (project: WithDb<ProjectOverview>, inArchive: boolean) => setDeleting({
    request: { dbId: project.db_id, dbName: project.db_name, projectId: project.id, name: project.display_name },
    inArchive,
  });
  // A project's Target Scheduler state, written straight into its database.
  // The notice shows where the change was made: a project reopened from the
  // archive leaves it, and the list's top may be out of view.
  const [stateNotice, setStateNotice] = useState<{ text: string; inArchive: boolean; focus?: boolean } | null>(null);
  const [pendingStates, setPendingStates] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!stateNotice) return;
    const timer = window.setTimeout(() => setStateNotice(null), 8000);
    return () => window.clearTimeout(timer);
  }, [stateNotice]);
  // Reopen takes the row, and its button, out of the archive: focus moves to
  // the notice, so a screen reader reads it and focus stays on the page.
  const archiveNotice = useRef<HTMLParagraphElement>(null);
  // A deletion from the Edit project panel closes the panel with the button
  // that started it: focus moves to the notice at the top of the list.
  const listNotice = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (stateNotice?.inArchive) archiveNotice.current?.focus();
    else if (stateNotice?.focus) listNotice.current?.focus();
  }, [stateNotice]);
  const changeState = async (project: WithDb<ProjectOverview>, state: number, inArchive = false) => {
    const key = projectKey(project.db_id, project.id);
    if (key in pendingStates) return;
    setPendingStates((current) => ({ ...current, [key]: state }));
    try {
      await apiClient.updateProject(project.db_id, project.id, { state });
      setStateNotice({ text: `${project.display_name} is now ${stateLabel(state)} on ${project.db_name}.`, inArchive });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['db', project.db_id] }),
        queryClient.invalidateQueries({ queryKey: ['directorPlans'] }),
      ]);
    } catch (error) {
      setStateNotice({ text: error instanceof Error ? error.message : 'Could not change the project state.', inArchive });
    } finally {
      setPendingStates((current) => { const next = { ...current }; delete next[key]; return next; });
    }
  };
  // The workspace for a plan, or for a project Planning has not adopted yet
  // the page that finds or starts its plan.
  // The project clicked becomes the scope, so the workspace opens that
  // database's editor and its back link marks the row in the Library.
  const openPlan = (project: WithDb<ProjectOverview>, plan: DirectorPlanRow | null) => {
    const scope = new URLSearchParams(location.search);
    scope.set('db', project.db_id);
    scope.set('project', String(project.id));
    keepScoringInScope(location.search, scope);
    navigate(plans.hrefFor(plan ?? `${project.db_id}:${project.id}`, scope));
  };

  useEffect(() => {
    const timer = window.setInterval(() => setRelativeNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  // Desktop mode: export straight to a local folder (hardlink-or-copy) via
  // the native picker — the server IS this machine, so downloading a zip of
  // our own files would be silly. Browser mode keeps the zip download link.
  const isTauri = isTauriApp();
  const [exportBusy, setExportBusy] = useState(false);
  // The database whose server export this page last started (or found
  // running); drives the progress line under the catalog summary.
  const [exportJobDb, setExportJobDb] = useState<string | null>(null);
  // The export the user clicked, awaiting its layout choice in the dialog.
  const [pendingExport, setPendingExport] = useState<ExportRequest | null>(null);
  // The AstroBin acquisition CSV the user asked for, shown in its dialog.
  const [pendingAstroBin, setPendingAstroBin] = useState<AstroBinExportRequest | null>(null);
  // The project the user asked to stack with WBPP, shown in its dialog.
  const [pendingWbpp, setPendingWbpp] = useState<WbppRunRequest | null>(null);
  // Seeds the dialog's layout choice; edited in the settings panel.
  const { data: exportSettings } = useQuery({
    queryKey: ['export-settings'],
    queryFn: apiClient.getExportSettings,
    staleTime: 5 * 60 * 1000,
  });
  const openExport = (
    kind: ExportRequest['kind'],
    dbId: string,
    scope: { project_id?: number; target_id?: number },
    label: string
  ) => {
    if (!exportBusy) setPendingExport({ kind, dbId, scope, label });
  };
  const handleLocalExport = async (
    dbId: string,
    scope: { project_id?: number; target_id?: number },
    label: string,
    choice: ExportChoice
  ) => {
    try {
      const dest = await tauriFileSystem.pickPath();
      if (!dest) return;
      setExportBusy(true);
      const summary = await apiClient.exportLocal(dbId, { dest, ...choice, ...scope });
      const placed =
        summary.copied + summary.linked + (summary.reflinked ?? 0) + (summary.symlinked ?? 0);
      const referenced = summary.referenced ?? 0;
      alert(
        `Exported ${label}: ${
          referenced > 0 && placed === 0
            ? `${referenced} file(s) left in place`
            : `${placed} file(s) placed`
        }` +
          `${summary.linked > 0 ? ` (${summary.linked} hardlinked)` : ''}` +
          `${(summary.symlinked ?? 0) > 0 ? ` (${summary.symlinked} linked to the originals)` : ''}` +
          `${summary.skipped_existing > 0 ? `, ${summary.skipped_existing} already present` : ''}` +
          `${summary.missing > 0 ? `, ${summary.missing} missing on disk` : ''}` +
          `${uncalibratedNote(summary) ? `, ${uncalibratedNote(summary)}` : ''}` +
          `${flatWarningNote(summary) ? `, ${flatWarningNote(summary)}` : ''}` +
          `${summary.errors > 0 ? `, ${summary.errors} ERRORS` : ''}\n\n${dest}`
      );
    } catch (err) {
      alert(`Export failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setExportBusy(false);
    }
  };
  // Which databases have an operator-configured export directory: those get
  // a server export instead of the zip download.
  const serverExportDir = (dbId: string) =>
    databases?.find((db) => db.id === dbId)?.export_directory;
  const handleServerExport = async (
    dbId: string,
    scope: { project_id?: number; target_id?: number },
    label: string,
    choice: ExportChoice
  ) => {
    try {
      setExportBusy(true);
      const status = await apiClient.startServerExport(dbId, {
        ...scope,
        ...choice,
        subdirectory: label,
        scope_label: label,
      });
      setExportJobDb(dbId);
      if (!status.started) {
        alert('An export is already running for this database; watch its progress below.');
      }
    } catch (err) {
      alert(`Export failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setExportBusy(false);
    }
  };
  const exportJob = useExportJob(exportJobDb);
  const exportJobLine = describeExportProgress(exportJob.progress);
  // Every database's current WBPP run, so one under way or just finished
  // shows here whichever tab or device started it.
  const wbppRuns = useWbppRuns(useMemo(() => (databases ?? []).map((db) => db.id), [databases]));
  const openWbppRun = (dbId: string) => {
    const progress = wbppRuns.get(dbId)?.progress;
    setPendingWbpp({
      dbId,
      scope: progress?.project_id != null ? { project_id: progress.project_id } : {},
      label: progress?.scope ?? 'WBPP run',
    });
  };
  const publishWbppStatus = (dbId: string, status: WbppRunStatus) =>
    queryClient.setQueryData(['db', dbId, 'wbpp-run'], status);
  const dismissWbppRun = async (dbId: string) => {
    try {
      publishWbppStatus(dbId, await apiClient.dismissWbppRun(dbId));
    } catch (error) {
      alert(error instanceof Error ? error.message : String(error));
    }
  };
  const removeQueuedWbppRun = async (dbId: string, queueId: string) => {
    try {
      publishWbppStatus(dbId, await apiClient.removeQueuedWbppRun(dbId, queueId));
    } catch (error) {
      alert(error instanceof Error ? error.message : String(error));
    }
  };

  // Persist an organize edit (rename, or a project merge), then refresh this DB's
  // overview queries so the new grouping shows up.
  const saveOrganize = async () => {
    if (!organizing) return;
    setOrganizeBusy(true);
    setOrganizeError('');
    try {
      if (organizing.kind === 'project') {
        if (organizing.mergeInto !== '') {
          if (
            !confirm(
              'Merge this project into the selected one? Its targets and images move over and this project is deleted.'
            )
          ) {
            setOrganizeBusy(false);
            return;
          }
          await apiClient.mergeProject(
            organizing.dbId,
            organizing.id,
            Number(organizing.mergeInto)
          );
        } else if (organizing.name.trim()) {
          await apiClient.updateProject(organizing.dbId, organizing.id, organizing.name.trim());
        }
      } else {
        // Moving a target goes through Move to another project..., which
        // previews the change first.
        if (organizing.name.trim()) {
          await apiClient.updateTarget(organizing.dbId, organizing.id, { name: organizing.name.trim() });
        }
      }
      queryClient.invalidateQueries({ queryKey: ['db', organizing.dbId] });
      setOrganizing(null);
    } catch (err) {
      setOrganizeError(err instanceof Error ? err.message : String(err));
    } finally {
      setOrganizeBusy(false);
    }
  };

  // Open the move preview for the target being edited. A name typed in the
  // panel is saved first, so the move does not discard it.
  const moveTargetAfterRename = async (currentName: string) => {
    if (!organizing || organizing.kind !== 'target') return;
    const edited = organizing;
    const name = edited.name.trim();
    let targetName = currentName;
    if (name && name !== currentName) {
      setOrganizeBusy(true);
      setOrganizeError('');
      try {
        await apiClient.updateTarget(edited.dbId, edited.id, { name });
        targetName = name;
        void queryClient.invalidateQueries({ queryKey: ['db', edited.dbId] });
      } catch (err) {
        setOrganizeError(err instanceof Error ? err.message : String(err));
        return;
      } finally {
        setOrganizeBusy(false);
      }
    }
    setOrganizing(null);
    setOrganizationScope({
      kind: 'move_target',
      dbId: edited.dbId,
      sourceTargetId: edited.id,
      sourceTargetName: targetName,
      sourceProjectId: edited.projectId,
    });
  };

  // Group targets by (db_id, project_id) since project IDs collide across DBs.
  const targetsByProject = useMemo(() => {
    const map: Record<string, WithDb<TargetOverview>[]> = {};
    for (const target of targets) {
      const key = `${target.db_id}:${target.project_id}`;
      (map[key] ||= []).push(target);
    }
    return map;
  }, [targets]);

  // Every project row the Library has, whatever its filters show: a plan
  // with none of these waits in the plans section below the list.
  const projectKeys = useMemo(() => new Set(projects.map((project) => projectKey(project.db_id, project.id))), [projects]);

  const projectsByDb = useMemo(() => {
    const map: Record<string, WithDb<ProjectOverview>[]> = {};
    for (const project of projects) {
      (map[project.db_id] ||= []).push(project);
    }
    return map;
  }, [projects]);

  const searchedProjects = useMemo(() => {
    const search = projectSearch.trim().toLocaleLowerCase();
    return projects.filter((project) => {
      if (dbFilter && project.db_id !== dbFilter) return false;
      if (projectMatchesSearch(project, projectSearch)) return true;
      if (!search) return true;
      return (targetsByProject[`${project.db_id}:${project.id}`] || []).some((target) =>
        target.name.toLocaleLowerCase().includes(search)
      );
    });
  }, [dbFilter, projectSearch, projects, targetsByProject]);

  // Show narrows by family: a plan shot by several rigs stays whole, and
  // matches a state when any rig has it (Closed and Done need every rig).
  const filteredProjects = useMemo(() => {
    if (show === 'all') return searchedProjects;
    const keep = new Set<string>();
    for (const family of projectFamilies(searchedProjects)) {
      const members = family.members.map((member) => ({
        ...member,
        calibration_level: calibrationHealth.get(projectKey(member.db_id, member.id))?.level ?? null,
      }));
      if (!familyMatchesShow(members, show)) continue;
      for (const member of family.members) keep.add(projectKey(member.db_id, member.id));
    }
    return searchedProjects.filter((project) => keep.has(projectKey(project.db_id, project.id)));
  }, [calibrationHealth, searchedProjects, show]);
  const narrowed = show !== 'all' || projectSearch.trim() !== '';

  const activeProjectGroups = useMemo(
    () =>
      groupProjectsByActivity(
        filteredProjects.filter((project) => !isArchivedProject(project)),
        relativeNow,
        projectSort
      ),
    [filteredProjects, projectSort, relativeNow]
  );

  const archivedProjects = useMemo(
    () =>
      sortProjects(
        filteredProjects.filter(isArchivedProject),
        projectSort
      ),
    [filteredProjects, projectSort]
  );

  // The scope the user came from. Returning to a long project list at the top
  // hides where they were, so mark that project and bring it into view once.
  const { dbId: scopeDbId, projectId: scopeProjectId } = useDbProjectTarget();
  const currentProjectKey =
    scopeDbId && scopeProjectId !== null ? projectKey(scopeDbId, scopeProjectId) : null;
  const revealedProjectKey = useRef<string | null>(null);
  const revealProject = useCallback(
    (node: HTMLElement | null) => {
      if (!node || !currentProjectKey) return;
      if (revealedProjectKey.current === currentProjectKey) return;
      revealedProjectKey.current = currentProjectKey;
      // jsdom has no layout, so guard for tests and older embedded webviews.
      node.scrollIntoView?.({ block: 'center' });
    },
    [currentProjectKey]
  );

  // An archived project is behind a collapsed section: open it so the card the
  // user is being sent back to exists.
  const currentIsArchived = archivedProjects.some(
    (project) => projectKey(project.db_id, project.id) === currentProjectKey
  );
  useEffect(() => {
    if (currentIsArchived) setArchivedOpen(true);
  }, [currentIsArchived]);

  const newestImageKey = useMemo(() => {
    let newest: { key: string; acquiredDate: number } | null = null;
    for (const project of projects) {
      for (const image of project.recent_images) {
        if (image.acquired_date === null) continue;
        const key = recentImageKey(project.db_id, project.id, image.id);
        if (
          newest === null ||
          image.acquired_date > newest.acquiredDate ||
          (image.acquired_date === newest.acquiredDate && key > newest.key)
        ) {
          newest = { key, acquiredDate: image.acquired_date };
        }
      }
    }
    return newest?.key ?? null;
  }, [projects]);

  // Seed both project and target baselines the first time this browser sees
  // them. Later refreshes can then show which target received new frames
  // without labeling the whole back catalog as new.
  useEffect(() => {
    setSeenProjects((current) => {
      // From what is stored now: another tab, or a visit since this page
      // read the markers, may have written ones it has not seen, and a seed
      // must not write over them.
      const next = { ...current, ...loadProjectSeenState() };
      let changed = false;

      for (const project of projects) {
        const key = projectSeenKey(project.db_id, project.id);
        if (!next[key]) {
          next[key] = markerForProject(project, targetsByProject[key] || []);
          changed = true;
        }
      }

      if (changed) saveProjectSeenState(next);
      return changed ? next : current;
    });
  }, [projects, targetsByProject]);

  // Helper functions
  const formatDate = (timestamp?: number) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const formatDateRange = (dateRange: DateRange) => {
    if (!dateRange.earliest || !dateRange.latest) return 'No dates';
    const start = formatDate(dateRange.earliest);
    const end = formatDate(dateRange.latest);
    const span = dateRange.span_days ? `${dateRange.span_days} days` : '';
    return span ? `${start} - ${end} (${span})` : `${start} - ${end}`;
  };

  const getGradingProgress = (accepted: number, rejected: number, pending: number) => {
    const total = accepted + rejected + pending;
    if (total === 0) return { acceptedPct: 0, rejectedPct: 0, pendingPct: 0 };
    
    return {
      acceptedPct: Math.round((accepted / total) * 100),
      rejectedPct: Math.round((rejected / total) * 100),
      pendingPct: Math.round((pending / total) * 100),
    };
  };

  const getDesiredProgress = (accepted: number, desired: number) => {
    if (desired === 0) return 0;
    return Math.round((accepted / desired) * 100);
  };

  // Navigation handlers. Each click carries the project's db_id so the scoped
  // view knows which database to query. Opening a project or target clears
  // its "new" badge here; the frames it counted stay marked new in Images
  // and Sequence until the next visit (see revisitProject).
  const markProjectSeen = (project: WithDb<ProjectOverview>) => {
    setSeenProjects((current) => {
      const key = projectSeenKey(project.db_id, project.id);
      const next = {
        ...current,
        [key]: revisitProject(current[key], project, targetsByProject[key] || []),
      };
      saveProjectSeenState(next);
      return next;
    });
  };

  const markTargetSeen = (target: WithDb<TargetOverview>) => {
    const key = projectSeenKey(target.db_id, target.project_id);
    setSeenProjects((current) => {
      const project = projects.find(
        (candidate) =>
          candidate.db_id === target.db_id && candidate.id === target.project_id
      );
      const existing =
        current[key] ??
        (project
          ? markerForProject(project, targetsByProject[key] || [])
          : {
              latestImage: target.date_range.latest ?? 0,
              totalImages: target.image_count,
              targets: {},
            });
      const next = {
        ...current,
        [key]: {
          ...existing,
          targets: {
            ...existing.targets,
            [String(target.id)]: revisitTarget(current[key], target),
          },
        },
      };
      saveProjectSeenState(next);
      return next;
    });
  };

  const handleSelectProject = (project: WithDb<ProjectOverview>) => {
    markProjectSeen(project);
    navigate(`/grid?db=${encodeURIComponent(project.db_id)}&project=${project.id}`);
  };

  const handleSelectImage = (
    project: WithDb<ProjectOverview>,
    image: ProjectRecentImage
  ) => {
    const target = (targetsByProject[projectSeenKey(project.db_id, project.id)] || [])
      .find((candidate) => candidate.id === image.target_id);
    if (target) markTargetSeen(target);
    else markProjectSeen(project);
    const params = new URLSearchParams({
      db: project.db_id,
      project: String(project.id),
      target: String(image.target_id),
    });
    navigateNow(imageDetailPath(image.id, params, 'grid'));
  };

  const handleSelectTarget = (target: WithDb<TargetOverview>) => {
    markTargetSeen(target);
    navigate(
      `/grid?db=${encodeURIComponent(target.db_id)}&project=${target.project_id}&target=${target.id}`
    );
  };

  // The review views beside Images. A project with one target opens at it,
  // since Sequence analyzes one target at a time.
  const openProjectView = (
    view: 'sequence' | 'stacks',
    project: WithDb<ProjectOverview>,
    targets: WithDb<TargetOverview>[]
  ) => {
    markProjectSeen(project);
    const params = new URLSearchParams({ db: project.db_id, project: String(project.id) });
    if (targets.length === 1) params.set('target', String(targets[0].id));
    navigate(`/${view}?${params.toString()}`);
  };

  const openTargetView = (view: 'sequence' | 'stacks', target: WithDb<TargetOverview>) => {
    markTargetSeen(target);
    const params = new URLSearchParams({
      db: target.db_id,
      project: String(target.project_id),
      target: String(target.id),
    });
    navigate(`/${view}?${params.toString()}`);
  };

  if (statsLoading || projectsLoading || targetsLoading) {
    return <div className="overview-loading">Loading overview...</div>;
  }

  if (!databases || databases.length === 0) {
    const managementAllowed = serverInfo?.allow_database_management ?? false;
    return (
      <div className="overview-empty">
        <h2>No databases configured</h2>
        {managementAllowed ? (
          <>
            <p>
              Build a catalog from folders of FITS or XISF images, import a
              Target Scheduler database, or connect PSF Guard Sync in N.I.N.A.
            </p>
            <div className="overview-empty-actions">
              <button
                className="action-button primary"
                onClick={() => openSettings('create')}
              >
                New Database from Images
              </button>
              <button
                className="action-button"
                onClick={() => openSettings('add')}
              >
                Import from Target Scheduler
              </button>
            </div>
          </>
        ) : (
          <>
            <p>
              This server doesn't permit configuration changes from the
              browser. Register a database on the command line:
            </p>
            <pre className="code-block">
              psf-guard server &lt;db.sqlite&gt; &lt;image-dir&gt;
            </pre>
            <p>
              …or restart with{' '}
              <code>--allow-database-management</code> to enable in-browser
              settings.
            </p>
          </>
        )}
        {/* A plan can exist before any database does. */}
        <LibraryPlans search="" show="all" listed={projectKeys} />
      </div>
    );
  }

  return (
    <div className="overview">
      {/* Overall Statistics */}
      {overallStats && (
        <section className="overview-summary" aria-label="Catalog summary">
          <div className="summary-lead">
            <span>Catalog</span>
            <strong>{overallStats.total_images.toLocaleString()} images</strong>
          </div>
          {(wbppRuns.size > 0 || exportJobLine) && (
            <ul className="overview-jobs" aria-label="Background jobs">
              {[...wbppRuns.entries()].map(([dbId, status]) => {
                const progress = status.progress;
                const line = isRunOfInterest(progress) ? describeWbppRun(progress) : null;
                return [
                  line && (
                    <li
                      key={`${dbId}-run`}
                      className={`overview-job overview-wbpp-run tone-${wbppRunTone(progress)}`}
                    >
                      <span className="overview-job-icon" aria-hidden="true">⚗</span>
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => openWbppRun(dbId)}
                      >
                        {line}
                      </button>
                      {!progress.running && progress.publish?.state !== 'running' && (
                        <button
                          type="button"
                          className="overview-job-dismiss"
                          aria-label={`Dismiss ${line}`}
                          title="Clear this run from view; its folder stays on disk"
                          onClick={() => void dismissWbppRun(dbId)}
                        >
                          ×
                        </button>
                      )}
                    </li>
                  ),
                  ...status.queued.map((entry) => (
                    <li
                      key={`${dbId}-${entry.id}`}
                      className="overview-job overview-wbpp-queued tone-queued"
                    >
                      <span className="overview-job-icon" aria-hidden="true">⚗</span>
                      <button
                        type="button"
                        className="link-button"
                        onClick={() =>
                          setPendingWbpp({
                            dbId,
                            scope:
                              entry.project_id != null
                                ? { project_id: entry.project_id }
                                : entry.target_id != null
                                  ? { target_id: entry.target_id }
                                  : {},
                            label: entry.scope,
                          })
                        }
                      >
                        {describeQueuedRun(entry)}
                      </button>
                      <button
                        type="button"
                        className="overview-job-dismiss"
                        aria-label={`Remove ${entry.scope} from the WBPP queue`}
                        title="Take this run out of the line"
                        onClick={() => void removeQueuedWbppRun(dbId, entry.id)}
                      >
                        ×
                      </button>
                    </li>
                  )),
                ];
              })}
              {exportJobLine && (
                <li
                  className={`overview-job overview-export-job tone-${
                    exportJob.progress?.stage === 'error'
                      ? 'error'
                      : exportJob.progress?.running
                        ? 'running'
                        : 'done'
                  }`}
                >
                  <span className="overview-job-icon" aria-hidden="true">⇩</span>
                  <span>{exportJobLine}</span>
                </li>
              )}
            </ul>
          )}
          <dl className="summary-metrics">
            <div>
              <dt>Projects</dt>
              <dd>
                {overallStats.active_projects}
                <span> / {overallStats.total_projects}</span>
              </dd>
            </div>
            <div>
              <dt>Targets</dt>
              <dd>
                {overallStats.active_targets}
                <span> / {overallStats.total_targets}</span>
              </dd>
            </div>
            <div>
              <dt>Accepted</dt>
              <dd>{overallStats.accepted_images.toLocaleString()}</dd>
            </div>
            <div className={overallStats.pending_images > 0 ? 'summary-needs-review' : ''}>
              <dt>To review</dt>
              <dd>{overallStats.pending_images.toLocaleString()}</dd>
            </div>
            <div className={overallStats.files_missing > 0 ? 'summary-has-warning' : ''}>
              <dt>Files</dt>
              <dd>
                {overallStats.files_missing > 0
                  ? `${overallStats.files_missing} missing`
                  : 'All found'}
              </dd>
            </div>
          </dl>
          <div className="summary-grading">
            <div className="summary-progress-label">
              <span>Grading</span>
              <span>
                {getGradingProgress(
                  overallStats.accepted_images,
                  overallStats.rejected_images,
                  overallStats.pending_images
                ).acceptedPct}% accepted
              </span>
            </div>
            <div
              className="summary-progress-bar"
              aria-label={`${overallStats.accepted_images} accepted, ${overallStats.rejected_images} rejected, ${overallStats.pending_images} pending`}
            >
              <div 
                className="progress-accepted" 
                style={{ 
                  width: `${getGradingProgress(
                    overallStats.accepted_images, 
                    overallStats.rejected_images, 
                    overallStats.pending_images
                  ).acceptedPct}%` 
                }}
              />
              <div 
                className="progress-rejected" 
                style={{ 
                  width: `${getGradingProgress(
                    overallStats.accepted_images, 
                    overallStats.rejected_images, 
                    overallStats.pending_images
                  ).rejectedPct}%` 
                }}
              />
              <div 
                className="progress-pending" 
                style={{ 
                  width: `${getGradingProgress(
                    overallStats.accepted_images, 
                    overallStats.rejected_images, 
                    overallStats.pending_images
                  ).pendingPct}%` 
                }}
              />
            </div>
          </div>
        </section>
      )}

      <div className="content-grid">
        <div className="projects-section">
          {/* The filters, then how the list is arranged, in the pills
              Images and Sequence use. Overview.css wraps them into rows as
              the width runs out. */}
          <div className="projects-toolbar">
            <div className="projects-toolbar-heading">
              <h2>Projects</h2>
              <p>Grouped by the latest captured frame.</p>
            </div>
            <div className="projects-toolbar-controls">
              <div className="projects-toolbar-filters">
                {databases && databases.length > 1 && (
                  <PillSelect
                    label="Database"
                    active={!!dbFilter}
                    pillClassName="project-db-filter"
                    value={dbFilter ?? 'all'}
                    onChange={(event) => updateParams({ dbfilter: event.target.value })}
                    aria-label="Filter projects by database"
                  >
                    <option value="all">All databases</option>
                    {databases.map((db) => (
                      <option key={db.id} value={db.id}>
                        {db.name}
                      </option>
                    ))}
                  </PillSelect>
                )}
                <PillSelect
                  label="Show"
                  active={show !== 'all'}
                  pillClassName="project-show"
                  value={show}
                  onChange={(event) => updateParams({ show: event.target.value })}
                  aria-label="Show projects"
                >
                  {SHOW_OPTIONS.filter((option) => plans.enabled || !option.planning).map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </PillSelect>
                <label className={`toolbar-search project-search${projectSearch ? ' is-set' : ''}`}>
                  <Search size={13} aria-hidden="true" />
                  <input
                    type="search"
                    value={projectSearch}
                    onChange={(event) => setProjectSearch(event.target.value)}
                    placeholder="Search projects or targets"
                    aria-label="Search projects or targets"
                  />
                </label>
              </div>
              <div className="projects-toolbar-view">
                <PillSelect
                  label="Sort"
                  pillClassName="project-sort"
                  pillTitle="Sort within groups"
                  value={projectSort}
                  onChange={(event) => setProjectSort(event.target.value as ProjectSort)}
                  aria-label="Sort within groups"
                >
                  <option value="recent">Newest first</option>
                  <option value="name">Name A–Z</option>
                  <option value="images">Most images</option>
                </PillSelect>
                <DensityToggle density={density} onChange={chooseDensity} />
              </div>
            </div>
          </div>

          {narrowed && (
            <p className="library-narrowed" role="status">
              Showing {filteredProjects.length} of {projects.length} projects.{' '}
              <button type="button" className="library-link-button" onClick={() => updateParams({ show: null, q: null })}>Show all</button>
            </p>
          )}
          {stateNotice && !stateNotice.inArchive && <p ref={listNotice} tabIndex={-1} className="library-narrowed" role="status">{stateNotice.text}</p>}

          {activeProjectGroups.length === 0 && archivedProjects.length === 0 && show !== 'unlinked' && (
            <div className="empty-state">
              {narrowed ? 'No projects or targets match.' : 'No projects with images yet.'}
            </div>
          )}

          {/* One column grid for every group: rows, families and group
              headings take its tracks through subgrid, so a pill kind sits
              at the same x down the whole list. */}
          <div className="library-groups">
          {activeProjectGroups.map((group) => {
            return (
              <section
                key={group.id}
                className={`project-activity-group ${group.id === 'recent' ? 'is-recent' : ''}`}
              >
                <div className="project-activity-heading">
                  <div>
                    <h3>{group.label}</h3>
                    {group.id === 'recent' && <span className="recent-group-badge">Recent</span>}
                  </div>
                  <span>
                    {group.projects.length} project{group.projects.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className={`projects-list${density === 'compact' ? ' is-compact' : ''}`}>
            {projectFamilies(group.projects).map((family) => {
            const inFamily = family.members.length > 1;
            const renderProject = (project: WithDb<ProjectOverview>) => {
              const plan = plans.planFor(project.db_id, project.id);
              const dbProjects = projectsByDb[project.db_id] || [];
              const progress = getGradingProgress(
                project.accepted_images,
                project.rejected_images,
                project.pending_images
              );
              const key = projectKey(project.db_id, project.id);
              const projectTargets = targetsByProject[key] || [];
              const projectNewImages = newImageCount(
                project,
                seenProjects,
                projectTargets
              );
              const displayedNewImages = Math.min(
                projectNewImages,
                project.recent_images.length
              );

              const isCurrent = key === currentProjectKey;
              // A compact row says what matters at a glance; the project the
              // user came from, and any row they open, shows the whole card.
              if (!showsCard(key, isCurrent)) {
                // One cell per Library column, so each pill kind lines up
                // down the list. The name's badges ride in its cell.
                return (
                  <div key={key} data-project-key={key} className={['library-row', !project.has_files ? 'no-files' : '', projectNewImages > 0 ? 'has-new-images' : ''].filter(Boolean).join(' ')} data-testid="library-row">
                    <RowCells cells={{
                      fold: <FoldButton open={false} name={project.display_name} onClick={() => toggleCard(key, isCurrent)} />,
                      name: <>
                        <button type="button" className="library-name" onClick={() => project.has_files && handleSelectProject(project)} disabled={!project.has_files} aria-label={`Open ${project.display_name} image grid`} title={project.display_name}>{project.display_name}</button>
                        {projectNewImages > 0 && <span className="new-images-badge"><span aria-hidden="true" />{projectNewImages} new</span>}
                        {!project.has_files && <span className="no-files-badge">No Files</span>}
                        {project.files_missing > 0 && <span className="library-pill files-missing">{project.files_missing} missing</span>}
                      </>,
                      db: <DbPill name={project.db_name} title={`Database ID: ${project.db_id}`} />,
                      state: <StateControl state={project.state} name={project.display_name} dbName={project.db_name} editable={organizeAllowed} pending={pendingStates[key] ?? null} onChange={(state) => void changeState(project, state)} />,
                      progress: <ProgressPill accepted={project.accepted_images} desired={project.total_desired} totalImages={project.total_images} />,
                      grades: <GradingPill accepted={project.accepted_images} rejected={project.rejected_images} pending={project.pending_images} />,
                      dates: <DatesPill earliest={project.date_range.earliest} latest={project.date_range.latest} nowMs={relativeNow} title={formatDateRange(project.date_range)} />,
                      calibration: <CalibrationPill health={calibrationHealth.get(key)} name={project.display_name} onOpen={() => openCalibrationReport(project)} />,
                      views: <>
                        <button type="button" className="library-view" disabled={!project.has_files} aria-label={`Open ${project.display_name} sequence`} title="Sequence analysis" onClick={() => openProjectView('sequence', project, projectTargets)}>Sequence</button>
                        <button type="button" className="library-view" disabled={!project.has_files} aria-label={`Open ${project.display_name} stacks`} title="Stack previews" onClick={() => openProjectView('stacks', project, projectTargets)}>Stacks</button>
                      </>,
                      plan: plans.enabled && !inFamily && <button type="button" className="library-planning" title="Open this project's plan: framing, rigs and activation" aria-label={`Open the ${project.display_name} plan`} onClick={() => openPlan(project, plan)}>⚙</button>,
                    }} />
                  </div>
                );
              }

              return (
                <div
                  key={key}
                  ref={isCurrent ? revealProject : undefined}
                  data-project-key={key}
                  data-current-project={isCurrent ? 'true' : undefined}
                  className={[
                    'project-card',
                    !project.has_files ? 'no-files' : '',
                    projectNewImages > 0 ? 'has-new-images' : '',
                    isCurrent ? 'is-current' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <div className="project-header">
                    <FoldButton open={true} name={project.display_name} onClick={() => toggleCard(key, isCurrent)} />
                    <button
                      type="button"
                      className="project-open-main"
                      onClick={() => project.has_files && handleSelectProject(project)}
                      disabled={!project.has_files}
                      aria-label={`Open ${project.display_name} image grid`}
                      aria-current={isCurrent ? 'true' : undefined}
                    >
                      <span className="project-title">{project.display_name}</span>
                      <span className="project-database" title={`Database ID: ${project.db_id}`}>
                        {project.db_name}
                      </span>
                      {isRecentProject(project, relativeNow) && (
                        <span className="project-recent-badge">Recent</span>
                      )}
                      {projectNewImages > 0 && (
                        <span className="new-images-badge">
                          <span aria-hidden="true" />
                          {projectNewImages} new
                        </span>
                      )}
                      <span className="project-open-label">
                        Open image grid
                        <span className="project-open-arrow" aria-hidden="true">→</span>
                      </span>
                    </button>
                    <div className="project-header-actions">
                      {!project.has_files && <span className="no-files-badge">No Files</span>}
                      <button
                        type="button"
                        className="project-settings-button"
                        disabled={!project.has_files}
                        aria-label={`Open ${project.display_name} sequence`}
                        title="Sequence analysis of this project's targets"
                        onClick={(e) => {
                          e.stopPropagation();
                          openProjectView('sequence', project, projectTargets);
                        }}
                      >
                        Sequence
                      </button>
                      <button
                        type="button"
                        className="project-settings-button"
                        disabled={!project.has_files}
                        aria-label={`Open ${project.display_name} stacks`}
                        title="Stack previews of this project"
                        onClick={(e) => {
                          e.stopPropagation();
                          openProjectView('stacks', project, projectTargets);
                        }}
                      >
                        Stacks
                      </button>
                      {plans.enabled && !inFamily && (
                        <button
                          type="button"
                          className="project-settings-button"
                          aria-label={`Open the ${project.display_name} plan`}
                          title="Open this project's plan: framing, rigs, activation, and its Target Scheduler targets and exposures"
                          onClick={(e) => {
                            e.stopPropagation();
                            openPlan(project, plan);
                          }}
                        >
                          <span aria-hidden="true">⚙</span>
                          Planning
                        </button>
                      )}
                      <button
                        type="button"
                        className="project-settings-button"
                        title="How the calibration library covers this project: matches, ages, and same-night flats"
                        onClick={(e) => {
                          e.stopPropagation();
                          openCalibrationReport(project);
                        }}
                      >
                        <span aria-hidden="true">🧪</span>
                        Calibration
                      </button>
                      {organizeAllowed && (
                        <button
                          type="button"
                          className="project-settings-button"
                          title="Rename or merge this project"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOrganizeError('');
                            setOrganizing({
                              kind: 'project',
                              dbId: project.db_id,
                              id: project.id,
                              name: project.name,
                              mergeInto: '',
                            });
                          }}
                        >
                          <span aria-hidden="true">✎</span>
                          Edit project
                        </button>
                      )}
                    </div>
                  </div>

                  {organizing?.kind === 'project' &&
                    organizing.dbId === project.db_id &&
                    organizing.id === project.id && (
                      <div className="organize-panel" onClick={(e) => e.stopPropagation()}>
                        <input
                          className="organize-input"
                          value={organizing.name}
                          onChange={(e) => setOrganizing({ ...organizing, name: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveOrganize();
                            if (e.key === 'Escape') setOrganizing(null);
                          }}
                          placeholder="Project name"
                          autoFocus
                        />
                        <select
                          className="organize-select"
                          value={organizing.mergeInto}
                          onChange={(e) =>
                            setOrganizing({ ...organizing, mergeInto: e.target.value })
                          }
                          title="Merge this project's targets and images into another project"
                        >
                          <option value="">(no merge)</option>
                          {dbProjects
                            .filter((p) => p.id !== project.id)
                            .map((p) => (
                              <option key={p.id} value={p.id}>
                                Merge into: {p.display_name}
                              </option>
                            ))}
                        </select>
                        <button
                          className="organize-save"
                          onClick={saveOrganize}
                          disabled={organizeBusy}
                        >
                          {organizing.mergeInto !== '' ? 'Merge' : 'Save'}
                        </button>
                        <button
                          className="organize-cancel"
                          onClick={() => setOrganizing(null)}
                          disabled={organizeBusy}
                        >
                          Cancel
                        </button>
                        {deleteAllowed && (
                          <button
                            type="button"
                            className="organize-delete"
                            disabled={organizeBusy}
                            onClick={() => openDelete(project, false)}
                          >
                            Delete project...
                          </button>
                        )}
                        {organizeError && <span className="organize-error">{organizeError}</span>}
                      </div>
                    )}

                  {project.description && (
                    <p className="project-description">{project.description}</p>
                  )}

                  {project.has_files && project.recent_images.length > 0 && (
                    <section
                      className="project-recent"
                      aria-label={`Recent frames for ${project.display_name}`}
                    >
                      <div className="project-recent-heading">
                        <span>
                          {displayedNewImages > 0
                            ? `${displayedNewImages}${projectNewImages > displayedNewImages ? ` of ${projectNewImages}` : ''} new frame${projectNewImages === 1 ? '' : 's'}`
                            : 'Recent frames'}
                        </span>
                        <span>Open a frame to inspect it</span>
                      </div>
                      <div className="project-recent-frames">
                        {project.recent_images.map((image, index) => {
                          const isNew = index < displayedNewImages;
                          const isNewest =
                            recentImageKey(project.db_id, project.id, image.id) ===
                            newestImageKey;
                          const filter = image.filter_name || 'No filter';
                          const relativeTime = formatRelativeTime(
                            image.acquired_date,
                            relativeNow
                          );
                          return (
                            <button
                              key={image.id}
                              type="button"
                              className={[
                                'project-frame',
                                isNew ? 'is-new' : '',
                                isNewest ? 'is-newest' : '',
                              ].filter(Boolean).join(' ')}
                              onClick={() => handleSelectImage(project, image)}
                              aria-label={`Open ${image.target_name}, ${filter} frame`}
                            >
                              <span className="project-frame-media">
                                <PreviewImage
                                  dbId={project.db_id}
                                  src={apiClient.getPreviewUrl(project.db_id, image.id, {
                                    size: 'screen',
                                    color,
                                  })}
                                  descriptor={{
                                    imageId: image.id,
                                    kind: 'preview',
                                    size: 'screen',
                                    color,
                                  }}
                                  alt={`${image.target_name}, ${filter}`}
                                  loading="lazy"
                                />
                                {isNew && <span className="project-frame-new">New</span>}
                                {isNewest && (
                                  <span
                                    className="project-frame-newest"
                                    title="Newest frame across all databases"
                                  >
                                    Newest
                                  </span>
                                )}
                              </span>
                              <span className="project-frame-caption">
                                <span className="project-frame-caption-main">
                                  <strong>{image.target_name}</strong>
                                  <span>{filter}</span>
                                </span>
                                {image.acquired_date === null ? (
                                  <span className="project-frame-age">{relativeTime}</span>
                                ) : (
                                  <time
                                    className="project-frame-age"
                                    dateTime={new Date(image.acquired_date * 1000).toISOString()}
                                    title={`Captured ${new Date(
                                      image.acquired_date * 1000
                                    ).toLocaleString()}`}
                                  >
                                    Captured {relativeTime}
                                  </time>
                                )}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </section>
                  )}
                  
                  <div className="project-stats">
                    <div className="stat-row">
                      <span>{project.total_images} images</span>
                      {project.total_desired > 0 && (
                        <>
                          <span>{project.accepted_images} / {project.total_desired} desired</span>
                          <span className="completion-badge">
                            {getDesiredProgress(project.accepted_images, project.total_desired)}% complete
                          </span>
                        </>
                      )}
                    </div>
                    <div className="stat-row">
                      <span>{project.accepted_images} accepted</span>
                      <span>{project.rejected_images} rejected</span>
                      <span>{project.pending_images} pending</span>
                    </div>
                    <div className="stat-row">
                      <span>{project.files_found} files found</span>
                      {project.files_missing > 0 && (
                        <span className="files-missing">{project.files_missing} missing</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Desired progress bar */}
                  {project.total_desired > 0 && (
                    <div className="project-desired-progress">
                      <div className="progress-label">Desired progress</div>
                      <div className="desired-progress-bar">
                        <div
                          className="desired-progress-fill"
                          style={{ width: `${getDesiredProgress(project.accepted_images, project.total_desired)}%` }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Grading status bar */}
                  <figure className="project-grading-progress">
                    <figcaption className="progress-label">Grading status</figcaption>
                    <div
                      className="project-mini-progress"
                      role="img"
                      aria-label={`Grading status: ${project.accepted_images} accepted, ${project.rejected_images} rejected, ${project.pending_images} pending`}
                    >
                      <div
                        className="mini-progress-accepted"
                        style={{ width: `${progress.acceptedPct}%` }}
                      />
                      <div
                        className="mini-progress-rejected"
                        style={{ width: `${progress.rejectedPct}%` }}
                      />
                      <div
                        className="mini-progress-pending"
                        style={{ width: `${progress.pendingPct}%` }}
                      />
                    </div>
                  </figure>
                  
                  <div className="project-meta">
                    <span>{formatDateRange(project.date_range)}</span>
                    <CalibrationPill
                      health={calibrationHealth.get(key)}
                      name={project.display_name}
                      onOpen={() => openCalibrationReport(project)}
                    />
                    {project.filters_used.length > 0 && (
                      <span>{project.filters_used.join(', ')}</span>
                    )}
                    {project.has_files && project.accepted_images > 0 && (
                      <span
                        className="export-link"
                        title={
                          isTauri
                            ? "Export this project's accepted lights to a local folder (hardlink or copy, rejects excluded)"
                            : serverExportDir(project.db_id)
                              ? `Export this project's accepted lights to the server's export directory (${serverExportDir(project.db_id)}), reflinking where the filesystem supports it`
                              : "Download this project's accepted lights as a zip (rejects excluded)"
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          openExport(
                            isTauri
                              ? 'local'
                              : serverExportDir(project.db_id)
                                ? 'server'
                                : 'download',
                            project.db_id,
                            { project_id: project.id },
                            project.display_name
                          );
                        }}
                      >
                        ⬇ Export
                      </span>
                    )}
                    {project.accepted_images + project.pending_images > 0 && (
                      <span
                        className="export-link"
                        title="The acquisition CSV AstroBin imports, one row per night, filter and exposure length"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingAstroBin({
                            dbId: project.db_id,
                            scope: { project_id: project.id },
                            label: project.display_name,
                          });
                        }}
                      >
                        ☆ AstroBin
                      </span>
                    )}
                    {organizeAllowed && project.accepted_images + project.pending_images > 0 && (() => {
                      const state = describeWbppRunForProject(wbppRuns.get(project.db_id), project.id);
                      return (
                        <span
                          className={`export-link${state ? ` wbpp-state wbpp-state-${state.tone}` : ''}`}
                          title={
                            state
                              ? "Open this project's WBPP run"
                              : "Stack this project with PixInsight's WBPP on the server, against the frames where they are"
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingWbpp({
                              dbId: project.db_id,
                              scope: { project_id: project.id },
                              label: project.display_name,
                            });
                          }}
                        >
                          ⚗ {state ? state.label : 'Stack in WBPP'}
                        </span>
                      );
                    })()}
                  </div>

                  {/* Targets stay visible so new work is easy to spot. */}
                  {projectTargets.length > 0 && (
                    <section className="project-targets-compact">
                      <div className="project-targets-heading">
                        <span>Targets</span>
                        <span>
                          {projectTargets.length} target
                          {projectTargets.length === 1 ? '' : 's'} · open one to filter the grid
                        </span>
                      </div>
                      <div className="project-targets-list">
                      {projectTargets.map((target) => {
                        const targetNewImages = newTargetImageCount(target, seenProjects);

                        return (
                          <div
                            key={target.id}
                            className={[
                              'target-compact-card',
                              !target.has_files ? 'no-files' : '',
                              targetNewImages > 0 ? 'has-new-images' : '',
                            ].filter(Boolean).join(' ')}
                          >
                            <button
                              type="button"
                              className="target-compact-main"
                              onClick={() => target.has_files && handleSelectTarget(target)}
                              disabled={!target.has_files}
                              aria-label={`Open ${target.name} image grid`}
                            >
                              <span className="target-compact-title">
                                <strong>{target.name}</strong>
                                {targetNewImages > 0 && (
                                  <span className="target-new-badge">
                                    {targetNewImages} new
                                  </span>
                                )}
                                {!target.has_files && (
                                  <span className="target-no-files">No files</span>
                                )}
                                <span
                                  className={`target-state ${target.active ? 'active' : 'inactive'}`}
                                >
                                  {target.active ? 'Active' : 'Inactive'}
                                </span>
                              </span>
                              <span className="target-compact-stats">
                                <span>{target.image_count} images</span>
                                <span>{target.accepted_count} accepted</span>
                                <span>{target.pending_count} pending</span>
                                {target.files_missing > 0 && (
                                  <span className="files-missing">{target.files_missing} missing</span>
                                )}
                                {target.filters_used.length > 0 && (
                                  <span>{target.filters_used.join(', ')}</span>
                                )}
                              </span>
                              {target.coordinates_display && (
                                <span className="target-compact-coordinates">
                                  {target.coordinates_display}
                                </span>
                              )}
                              <span className="target-compact-open" aria-hidden="true">
                                Open target →
                              </span>
                            </button>

                            <div className="target-compact-actions">
                              <button
                                type="button"
                                className="target-settings-button"
                                disabled={!target.has_files}
                                aria-label={`Open ${target.name} sequence`}
                                title="Sequence analysis of this target"
                                onClick={() => openTargetView('sequence', target)}
                              >
                                Sequence
                              </button>
                              <button
                                type="button"
                                className="target-settings-button"
                                disabled={!target.has_files}
                                aria-label={`Open ${target.name} stacks`}
                                title="Stack previews of this target"
                                onClick={() => openTargetView('stacks', target)}
                              >
                                Stacks
                              </button>
                              {organizeAllowed && (
                                <button
                                  type="button"
                                  className="target-settings-button organization-action"
                                  aria-label={`Merge target ${target.name}`}
                                  title="Merge this target into another target"
                                  onClick={() => setOrganizationScope({
                                    kind: 'merge_targets',
                                    dbId: target.db_id,
                                    sourceTargetId: target.id,
                                    sourceTargetName: target.name,
                                    sourceProjectId: target.project_id,
                                  })}
                                >
                                  <Merge size={14} aria-hidden="true" /> Merge target
                                </button>
                              )}
                              {organizeAllowed && (
                                <button
                                  type="button"
                                  className="target-settings-button"
                                  aria-label={`Edit target ${target.name}`}
                                  title="Rename this target or move it to another project"
                                  onClick={() => {
                                    setOrganizeError('');
                                    setOrganizing({
                                      kind: 'target',
                                      dbId: target.db_id,
                                      id: target.id,
                                      projectId: target.project_id,
                                      name: target.name,
                                    });
                                  }}
                                >
                                  ✎ Edit
                                </button>
                              )}
                              {target.has_files && target.accepted_count > 0 && (
                                <button
                                  type="button"
                                  className="target-settings-button"
                                  title={
                                    isTauri
                                      ? "Export this target's accepted lights to a local folder"
                                      : serverExportDir(target.db_id)
                                        ? `Export this target's accepted lights to the server's export directory (${serverExportDir(target.db_id)})`
                                        : "Download this target's accepted lights as a zip"
                                  }
                                  onClick={() =>
                                    openExport(
                                      isTauri
                                        ? 'local'
                                        : serverExportDir(target.db_id)
                                          ? 'server'
                                          : 'download',
                                      target.db_id,
                                      { target_id: target.id },
                                      target.name
                                    )
                                  }
                                >
                                  ↓ Export
                                </button>
                              )}
                              {target.accepted_count + target.pending_count > 0 && (
                                <button
                                  type="button"
                                  className="target-settings-button"
                                  title="The acquisition CSV AstroBin imports, one row per night, filter and exposure length"
                                  onClick={() =>
                                    setPendingAstroBin({
                                      dbId: target.db_id,
                                      scope: { target_id: target.id },
                                      label: target.name,
                                    })
                                  }
                                >
                                  ☆ AstroBin
                                </button>
                              )}
                            </div>

                            {organizing?.kind === 'target' &&
                              organizing.dbId === target.db_id &&
                              organizing.id === target.id && (
                              <div className="organize-panel">
                                <input
                                  className="organize-input"
                                  value={organizing.name}
                                  onChange={(e) =>
                                    setOrganizing({ ...organizing, name: e.target.value })
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveOrganize();
                                    if (e.key === 'Escape') setOrganizing(null);
                                  }}
                                  placeholder="Target name"
                                  aria-label="Target name"
                                  autoFocus
                                />
                                <button
                                  type="button"
                                  className="organize-move"
                                  title="Preview moving this target, its exposures and its exposure plans to another project or a new one"
                                  disabled={organizeBusy}
                                  onClick={() => void moveTargetAfterRename(target.name)}
                                >
                                  Move to another project...
                                </button>
                                <button
                                  className="organize-save"
                                  onClick={saveOrganize}
                                  disabled={organizeBusy}
                                >
                                  Save
                                </button>
                                <button
                                  className="organize-cancel"
                                  onClick={() => setOrganizing(null)}
                                  disabled={organizeBusy}
                                >
                                  Cancel
                                </button>
                                {organizeError && (
                                  <span className="organize-error">{organizeError}</span>
                                )}
                              </div>
                              )}
                          </div>
                        );
                      })}
                      </div>
                    </section>
                  )}
                </div>
              );
            };
            if (!inFamily) return renderProject(family.members[0]);
            // One plan across the rigs: its stage and its workspace sit on the outer pill.
            const planned = family.members.map((member) => ({ member, plan: plans.planFor(member.db_id, member.id) })).find((entry) => entry.plan);
            const familyPlan = planned?.plan ?? null;
            const planMember = planned?.member ?? family.members[0];
            const rigNames = family.members.map(member => member.db_name).join(' · ');
            return (
              <div key={family.key} className={`library-family${density === 'compact' ? ' is-compact' : ''}`} data-testid="library-family">
                <div className="library-family-head">
                  <RowCells cells={{
                    name: <>
                      <FamilyPill rigs={family.members.length} />
                      <strong className="library-family-name" title={family.name}>{family.name}</strong>
                    </>,
                    db: <DbPill name={rigNames} title={rigNames} />,
                    state: familyPlan?.activation
                      ? <span className="director-stage-badge">Activated on {familyPlan.activation.rigs} rig{familyPlan.activation.rigs === 1 ? '' : 's'}</span>
                      : familyPlan && (familyPlan.framing || familyPlan.plan) && <span className="library-pill library-pill-stage" title={stage(familyPlan)}>{stageShort(familyPlan)}</span>,
                    progress: <ProgressPill accepted={family.accepted} desired={family.desired} totalImages={family.totalImages} title={`${family.accepted} of ${family.desired} desired frames accepted across the rigs`} />,
                    dates: family.latest !== null && <DatesPill earliest={family.earliest} latest={family.latest} nowMs={relativeNow} title={`Across the rigs: ${formatDateRange({ earliest: family.earliest ?? undefined, latest: family.latest })}`} />,
                    plan: plans.enabled && <button type="button" className="library-planning" title="Open this plan: framing, rigs and activation" aria-label={`Open the ${family.name} plan`} onClick={() => openPlan(planMember, familyPlan)}>⚙</button>,
                  }} />
                </div>
                <div className="library-family-members">{family.members.map(renderProject)}</div>
              </div>
            );
          })}
                </div>
              </section>
            );
          })}
          </div>

          <LibraryPlans search={projectSearch} show={show} listed={projectKeys} dbFilter={dbFilter} incomplete={projectsIncomplete} />

          {/* Outside the archive: reopening its last project removes it. */}
          {stateNotice?.inArchive && <p ref={archiveNotice} tabIndex={-1} className="library-narrowed" role="status">{stateNotice.text}</p>}
          {archivedProjects.length > 0 && (
            <section className="project-archive">
              <button
                type="button"
                className="project-archive-toggle"
                onClick={() => setArchivedOpen((open) => !open)}
                aria-expanded={archivedOpen || narrowed}
              >
                <span
                  className={`expand-toggle ${archivedOpen || narrowed ? 'expanded' : ''}`}
                  aria-hidden="true"
                >
                  ▶
                </span>
                <span>Archived projects</span>
                <span>{archivedProjects.length}</span>
              </button>
              {(archivedOpen || narrowed) && (
                <div className="project-archive-list">
                  {archivedProjects.map((project) => {
                    const key = projectKey(project.db_id, project.id);
                    const isCurrent = key === currentProjectKey;
                    const reopening = key in pendingStates;
                    return (
                      <div
                        key={key}
                        ref={isCurrent ? revealProject : undefined}
                        data-project-key={key}
                        data-current-project={isCurrent ? 'true' : undefined}
                        className={`project-archive-item${isCurrent ? ' is-current' : ''}`}
                      >
                        <button
                          type="button"
                          className="project-archive-open"
                          aria-current={isCurrent ? 'true' : undefined}
                          onClick={() => project.has_files && handleSelectProject(project)}
                          disabled={!project.has_files}
                        >
                          <span>
                            <strong>{project.display_name}</strong>
                            <small>{project.db_name}</small>
                          </span>
                          <span>
                            {project.total_images} images
                            {project.date_range.latest
                              ? ` · ${formatRelativeTime(project.date_range.latest, relativeNow)}`
                              : ''}
                          </span>
                        </button>
                        {organizeAllowed && (
                          <button
                            type="button"
                            className="library-row-button"
                            disabled={reopening}
                            aria-label={`Reopen ${project.display_name} on ${project.db_name}`}
                            title={`Set ${project.display_name} to Inactive on ${project.db_name}. Target Scheduler does not select it until it is set to Active.`}
                            onClick={() => void changeState(project, INACTIVE_PROJECT_STATE, true)}
                          >
                            {reopening ? 'Reopening...' : 'Reopen'}
                          </button>
                        )}
                        {deleteAllowed && (
                          <button
                            type="button"
                            className="library-row-button library-row-delete"
                            aria-label={`Delete ${project.display_name} from ${project.db_name}`}
                            title={`Delete ${project.display_name} from ${project.db_name}. The dialog shows what goes before anything is deleted.`}
                            onClick={() => openDelete(project, true)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {deleteAllowed && (
            <DeletedProjects
              catalogs={(databases ?? [])
                .filter((database) => !dbFilter || database.id === dbFilter)
                .map((database) => ({ id: database.id, name: database.name }))}
            />
          )}
        </div>
      </div>

      {deleting && (
        <ProjectDeleteDialog
          request={deleting.request}
          onClose={() => setDeleting(null)}
          onDeleted={(outcome) => {
            const { request, inArchive } = deleting;
            setDeleting(null);
            setOrganizing(null);
            const frames = `${outcome.frames.toLocaleString()} frame${outcome.frames === 1 ? '' : 's'}`;
            const targets = `${outcome.targets} target${outcome.targets === 1 ? '' : 's'}`;
            setStateNotice({
              text: [
                `Deleted ${outcome.name} from ${request.dbName}, with ${targets} and ${frames}.`,
                outcome.frames > 0 ? 'The image files are still on disk.' : '',
                outcome.plan ? `Its plan, ${outcome.plan}, no longer plans work for this rig.` : '',
                outcome.plan_error ? `Planning still lists the project: ${outcome.plan_error}` : '',
              ].filter(Boolean).join(' '),
              inArchive,
              focus: true,
            });
          }}
        />
      )}

      {organizationScope && (
        <OrganizationDialog
          scope={organizationScope}
          onClose={() => setOrganizationScope(null)}
        />
      )}
      {pendingExport && (
        <ExportDialog
          request={pendingExport}
          defaultLayout={exportSettings?.default_layout ?? 'standard'}
          defaultWbpp={exportSettings?.wbpp ?? DEFAULT_WBPP_OPTIONS}
          sourceRoot={commonDirectory(
            databases?.find((db) => db.id === pendingExport.dbId)?.image_directories ?? []
          )}
          busy={exportBusy}
          onClose={() => setPendingExport(null)}
          onConfirm={(choice) => {
            const request = pendingExport;
            setPendingExport(null);
            if (request.kind === 'local') {
              handleLocalExport(request.dbId, request.scope, request.label, choice);
            } else {
              handleServerExport(request.dbId, request.scope, request.label, choice);
            }
          }}
        />
      )}

      {pendingAstroBin && (
        <AstroBinExportDialog request={pendingAstroBin} onClose={() => setPendingAstroBin(null)} />
      )}
      {pendingWbpp && (
        <WbppRunDialog
          request={pendingWbpp}
          defaultOptions={exportSettings?.wbpp ?? DEFAULT_WBPP_OPTIONS}
          onClose={() => setPendingWbpp(null)}
        />
      )}

      {calibrationReportProject && (
        <CalibrationReportDialog
          open
          dbId={calibrationReportProject.dbId}
          projectId={calibrationReportProject.id}
          projectName={calibrationReportProject.name}
          onClose={() => setCalibrationReportProject(null)}
        />
      )}

      {/* Footer with GitHub and License Info */}
      <div className="overview-footer">
        <div className="footer-content">
          <p>
            PSF Guard is open source software available on{' '}
            <a 
              href="https://github.com/theatrus/psf-guard" 
              target="_blank" 
              rel="noopener noreferrer"
              className="github-link"
            >
              GitHub
            </a>
          </p>
          <p className="license-info">
            Licensed under the Apache License 2.0
          </p>
        </div>
      </div>
    </div>
  );
}
